// src/components/household-contents/document/DocumentViewer/DocumentPreviewModal.tsx

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { useTranslation } from "next-i18next";

import { Document } from "@/api/rest/models/document";
import { downloadDocument2, getDocumentUrl2, logDocumentPreviewEvent } from "@/api/rest/services/documentService";

import ConfirmationDialog from "@/components/common/ConfirmationDialog.tsx";

import { DocumentViewer } from "./DocumentViewer";
import { getDocumentType } from "./types";
import { isOfficeDocument } from "./viewers/OfficeViewer";

interface DocumentPreviewModalProps {
  document: Document;
  trigger: React.ReactNode;
  onDownload?: (url: string, originalName: string) => void;
  onDelete?: (documentId: number) => Promise<void>;
  buildingId?: number;
  householdId?: number;
}

export function DocumentPreviewModal({
  document,
  trigger,
  onDownload,
  onDelete,
  buildingId,
  householdId,
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteConfirmationPending, setIsDeleteConfirmationPending] = useState(false);

  const handleDownload = () => {
    if (isOfficeDocument(getDocumentType(document))) {
      logDocumentPreviewEvent(document.id, "OFFICE", "DOWNLOAD");
    }
    if (onDownload) {
      onDownload(getDocumentUrl2(document), document.originalFilename);
    } else {
      downloadDocument2(getDocumentUrl2(document), document.originalFilename);
    }
  };

  const handleDelete = async () => {
    if (onDelete && isDeleteConfirmationPending) {
      setIsDeleteConfirmationPending(true);
      await onDelete(document.id);
      setIsOpen(false);
    }
  };

  const previewDelete = () => {
    setIsDeleteConfirmationPending(true);
  };

  const cancelDelete = () => {
    setIsDeleteConfirmationPending(false);
  };

  return (
    <>
      <div onClick={() => setIsOpen(true)}>{trigger}</div>

      <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-0 md:p-4">
          <DialogPanel className="bg-white flex flex-col shadow-xl w-full h-full md:w-[90%] md:h-[85vh] max-w-[1200px] md:rounded-lg">
            <div className="p-4 md:p-6 bg-slate-100 flex-none">
              <DialogTitle className="text-lg font-medium text-gray-900 flex flex-row justify-between items-center">
                <span className="break-all pr-3 text-sm md:text-lg">{document.fileName}</span>

                <div className="flex flex-row items-center">
                  <button
                    onClick={handleDownload}
                    className="rounded text-xs md:text-sm leading-6 px-2 mx-1 md:mx-2 !text-slate-100 hover:bg-dobu-orange-focus focus-visible:bg-dobu-orange-focus cursor-pointer self-baseline"
                  >
                    Download
                  </button>

                  {onDelete && (
                    <button
                      onClick={previewDelete}
                      className="rounded text-xs md:text-sm leading-6 px-2 mx-1 md:mx-2 !bg-red-800 hover:bg-dobu-orange-focus focus-visible:bg-dobu-orange-focus cursor-pointer self-baseline"
                    >
                      Delete
                    </button>
                  )}

                  <XCircleIcon
                    className="h-6 w-6 min-w-6 min-h-6 ml-2 text-gray-500 hover:text-dobu-orange-focus focus-visible:text-dobu-orange-focus cursor-pointer"
                    onClick={() => setIsOpen(false)}
                  />
                </div>
              </DialogTitle>
            </div>

            <div className="flex-grow relative overflow-hidden min-h-0">
              <DocumentViewer
                document={document}
                buildingId={buildingId}
                householdId={householdId}
                className="h-full"
              />
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      <ConfirmationDialog
        isOpen={isDeleteConfirmationPending}
        onConfirm={handleDelete}
        onCancel={cancelDelete}
        title={t("files.confirmDeleteTitle")}
        message={t("files.confirmDeleteBody")}
        confirmText={t("files.confirmDeleteYes")}
        cancelText={t("files.confirmDeleteNo")}
      />
    </>
  );
}
